import { createContext, useContext } from 'react';
import type { PlanStarter, TodayRestHours } from '@heraion/shared';

import type { FieldFocusSignal } from './useFieldFocus';
import type { useLoggerScreen } from './useLoggerScreen';
import type { LoggerCursor } from './useLoggerCursor';
import type { PinnedExercise } from './usePinnedExercise';
import type { useRestAdvance } from './useRestAdvance';
import type { UseRestInvite } from './useRestInvite';

// Shared logger context bag. Carries the full useLoggerScreen return so the
// logger subtree (the timeline screen) can read it without threading ~35 props
// down from LoggerScreen. A single context (not a stable/volatile split) is
// deliberate: no card is React.memo'd and re-render optimisation is out of
// scope, so a split would be behaviour-identical while adding surface.
//
// Provider lives in LoggerScreen. Extracted out of the now-removed
// LoggerEntryList.tsx (card logger) so the context survives that deletion —
// the timeline logger reads the same bag. The legacy `EntryList` name is kept
// to avoid churn across the timeline importers; it no longer implies a card.
// The bag = useLoggerScreen's return PLUS the focus cursor (useLoggerCursor),
// which is owned in the LoggerScreen body (above the Modal) so it survives
// minimize -> resume (LG-13). Both are merged into the provider value there.
type LoggerContextValue = ReturnType<typeof useLoggerScreen> &
  LoggerCursor & {
    /** The post-log focus transition. Lives above the Modal beside the cursor
     *  (LG-13): it holds a decision taken but not yet applied, and a minimize
     *  mid-rest would otherwise destroy it while the timer kept counting. */
    restAdvance: ReturnType<typeof useRestAdvance>;
    /** #308: the pinned row, read by `restAdvance`. Above the Modal with it. */
    pin: PinnedExercise;
    /** The longer-rest offer on the running rest. Above the Modal for the
     *  same LG-13 reason: it belongs to a countdown that survives minimize. */
    restInvite: UseRestInvite;
    /** Whether a field currently holds the keyboard. Owned above the Modal for
     *  the same reason (LG-13) and read by the rest-end resume, which must not
     *  pull the caret out of a half-typed number. */
    fieldFocus: FieldFocusSignal;
    /** PE-44 — how many weeks the FOCUSED exercise would lose if it were
     *  removed. Only a prescribing mount knows this: in the logger a removal
     *  costs the one session in front of you, in the editor it deletes the
     *  slot from every week it runs. Optional, and absent is the logger's
     *  honest answer — `useEntryDialogs` keeps today's wording without it.
     *  ⛔ A COUNT, not a sentence: the plural rules are i18next's job, and
     *  five locales do not agree on how many forms "N weeks" has. */
    removeExerciseAffectedWeeks?: number;
    /** AP-9b: the host's door to the pain map; absent, no link. */
    onOpenPainMap?: () => void;
    /** The empty «today» card's exits (see useTodayDraft). */
    onBuildAnyway?: () => Promise<PlanStarter | null>;
    onOpenCreateHub?: () => void;
    /** The draft's rest chips save the account's choice (see useTodayDraft). */
    onTodayRestHours?: (hours: TodayRestHours) => void;
    weeklyMinimumMet?: boolean;
    onTodayEmptyChange?: (empty: boolean, bodyweightOnly?: boolean) => void;
  };

/** Exactly what `useTimelineLogger` reads off this context — nothing else.
 *
 *  Lives HERE, beside the wide bag it narrows, and not beside the hook: the
 *  question it answers is "what must a mount supply", which is this file's
 *  subject. (It also kept `useTimelineLogger` under the 500-line cap, which is
 *  how the placement got asked in the first place.)
 *
 *  ⭐ PE-22 K2. It used to ask for the WHOLE `ReturnType<typeof useLoggerScreen>`,
 *  which is the mega-hook that instantiates the rest timer, the hold timer, the
 *  draft persistence and the save path — everything a PRESCRIPTION must not
 *  instantiate. Asking for the whole bag meant the only way to mount this focus
 *  machine outside a live session was to fabricate a fake session, and a fake
 *  cast to `never` is a contract switched off, not a contract met
 *  ([[feedback_a_test_double_cast_is_a_disabled_contract]] — and the cast was
 *  really there, in loggerOneTapTwoPrompts.test.tsx).
 *
 *  The fields stay `Pick`ed off the real context instead of being re-declared
 *  by hand: one source of truth, so a signature that changes upstream still
 *  breaks loudly rather than drifting into a second definition.
 *
 *  ⛔ Adding a field here is a decision, not a formality: it widens what any
 *  mount must supply, and the whole point is that the editor supplies a small,
 *  nameable set. `deps` is narrowed to `confirm` alone for the same reason —
 *  it is the only thing read (`ctx.deps.confirm`, the ⊗ split-group and ✕
 *  remove-exercise dialogs). */
export type TimelineLoggerInput = Pick<
  LoggerContextValue,
  | 'draft'
  | 'dispatch'
  | 't'
  | 'profile'
  | 'getExerciseNameBySlug'
  | 'catalog'
  | 'defaultUnit'
  | 'cycleSetPill'
  | 'mode'
  | 'restTimer'
  | 'restPicker'
  | 'restAdvance'
  | 'activeId'
  | 'setActiveId'
  | 'selectedSetIdx'
  | 'setSelectedSetIdx'
  | 'rosterExpanded'
  | 'setRosterExpanded'
  | 'removeExerciseAffectedWeeks'
> &
  // Optional: a mount with no live rest (the prescribe editor) has no offer,
  // no hold timer for an inserted set to move, and no pin for a log to set.
  Partial<Pick<LoggerContextValue, 'restInvite' | 'hold' | 'pin'>> & {
    deps: Pick<LoggerContextValue['deps'], 'confirm'>;
  };

/** Exactly what `TimelineFocusSection` reads off this context — nothing else.
 *  Same recipe as `TimelineLoggerInput` above. ⛔ Adding a field here is a
 *  decision, not a formality (PE-22 K2a): it widens what a mount must supply.
 *  `deps` is narrowed to `confirm` alone, the only thing this section reads. */
export type TimelineFocusSectionInput = Pick<
  LoggerContextValue,
  | 'draft' | 'dispatch' | 'theme' | 't' | 'i18n'
  | 'getExerciseNameBySlug' | 'groupLabelFor' | 'groupIndexFor' | 'defaultUnit'
  | 'setPickerOpen' | 'setEffortScaleOpen' | 'setTipEntryIdx'
  | 'setSwapEntryIdx' | 'setGroupPickerEntryIdx' | 'setSupersetMenu'
  | 'plate' | 'hold' | 'timerDefaults' | 'mode'
> & { deps: Pick<LoggerContextValue['deps'], 'confirm'> };

/** Exactly what `TimelineFeedSection` reads off this context — nothing else.
 *  Same recipe as `TimelineLoggerInput` above. ⛔ Adding a field here widens
 *  what a mount must supply (PE-22 K2a). */
export type TimelineFeedSectionInput = Pick<
  LoggerContextValue,
  'draft' | 'theme' | 't' | 'i18n' | 'getExerciseNameBySlug' | 'groupLabelFor'
>;

/** Exactly what `useTimelineNotes` reads off this context — nothing else.
 *  Same recipe as `TimelineLoggerInput` above. ⛔ Adding a field here widens
 *  what a mount must supply (PE-22 K2a). */
export type TimelineNotesInput = Pick<
  LoggerContextValue,
  'draft' | 'dispatch' | 'mode'
>;

export const LoggerContext = createContext<LoggerContextValue | null>(
  null,
);

export function useLoggerContext(): LoggerContextValue {
  const ctx = useContext(LoggerContext);
  if (ctx === null) {
    throw new Error(
      'Logger subtree must be rendered inside LoggerContext.Provider',
    );
  }
  return ctx;
}
